// First-visit wizard: collects name, UI locale, target/source langs,
// optional level floor, direction ratio, and font scale. Owns
// renderWizard / startWizard / finishWizard plus the wizard-specific
// font-scale and dir-slider wiring.
//
// applyLocale is injected via deps because it lives in bootstrap —
// refreshing ~80 static text nodes is a bootstrap concern (it touches
// DOM scattered across every feature), not a wizard concern.

import { LEVELS, type Level } from "../config/defaults";
import {
  LANG_NAMES,
  LANG_NATIVE,
  LANG_SHORT,
  LANG_TO_LOCALE,
  LANGS,
  LOCALE_TO_LANG,
  type Lang,
} from "../config/langs";
import { DEFAULT_LIST_ID, defaultListForTheme } from "../config/lists";
import { STORAGE_KEYS } from "../config/storage-keys";
import { DEFAULT_THEME_ID, THEMES, getTheme } from "../config/themes";
import { currentLocale, t } from "../i18n";
import { saveDirRatio, saveLevelFloor } from "../state/settings";
import type { CefrLevel } from "../types";
import { byId, hide, show } from "./dom";
import { changeFontScale, loadFontScale } from "./font-scale";
import { openTour } from "./tour";
import { renderDirPresets } from "./dir-presets";

/** "none" means no floor (adaptive estimator starts fresh). Any CEFR
 *  level acts as a floor that the estimator can PROMOTE above but
 *  never drops below. */
type LevelFloorPick = "none" | Level;

type WizardPicks = {
  locale: string | null;
  /** Learning theme id (cf. config/themes.ts). Gates the rest of the
   *  flow: non-"languages" themes hide the lang pair + CEFR sections
   *  and commit only the user-level settings. */
  themeId: string;
  target: Lang | null;
  source: Lang | null;
  userName: string | null;
  levelFloor: LevelFloorPick;
  /** 0..100. 20 = 80% target→source (read target, write source). */
  dirRatio: number;
};

export type WizardDeps = {
  // AppState reads — pre-fill the form when the wizard is re-opened.
  getTargetLang: () => Lang | null;
  getSourceLang: () => Lang | null;
  getUserName: () => string;
  getLevelFloor: () => CefrLevel | null;
  // Writes (commit on finish).
  setTargetLang: (lang: Lang) => void;
  setSourceLang: (lang: Lang) => void;
  setUserName: (name: string) => void;
  setLevelFloor: (lv: CefrLevel | null) => void;
  setUserLevel: (lv: CefrLevel) => void;
  setDirRatio: (r: number) => void;
  /** Theme + deck setters — called on finish so the in-memory state
   *  matches the localStorage commits before loadList fires (otherwise
   *  the loaded deck would lag the user's picks by one reload). */
  setThemeId: (id: string) => void;
  setListId: (id: string) => void;
  /** Refreshes all static UI text post locale change. Lives in
   *  bootstrap because it touches DOM owned by every feature. */
  applyLocale: () => void;
  /** Load the currently-selected card list after wizard commits. */
  loadList: () => void;
};

export type Wizard = {
  startWizard: () => void;
  finishWizard: () => void;
};

export function createWizard(deps: WizardDeps): Wizard {
  // Local picks — not committed until "Continue" clicks through validation.
  const wizardPicks: WizardPicks = {
    locale: null,
    themeId: DEFAULT_THEME_ID,
    target: null,
    source: null,
    userName: null,
    levelFloor: "none",
    dirRatio: 20,
  };

  // Element refs captured lazily — the wizard screen exists in the DOM
  // from the start, so these are safe to resolve at factory-init time.
  const wizardScreen = byId("wizardScreen");
  const wizardLocaleSelect = byId<HTMLSelectElement>("wizardLocaleSelect");
  const wizardTargetSelect = byId<HTMLSelectElement>("wizardTargetSelect");
  const wizardSourceSelect = byId<HTMLSelectElement>("wizardSourceSelect");
  const wizardContinue = byId<HTMLButtonElement>("wizardContinue");
  const wizardError = byId("wizardError");
  const wizardLevelPills = byId("wizardLevelPills");
  const wizardNameInput = byId<HTMLInputElement>("wizardName");
  const wizardNameLabel = byId("wizardNameLabel");

  if (wizardNameInput) {
    wizardNameInput.addEventListener("input", () => {
      wizardPicks.userName = wizardNameInput.value.trim() || null;
    });
  }

  function renderWizard(): void {
    const loc = (wizardPicks.locale || currentLocale()) as keyof typeof LANG_NAMES;
    // Theme pills — always visible. One per THEMES entry, icon + name.
    // Gates the visibility of the language-specific section below.
    const themeHost = byId("wizardThemePills");
    if (themeHost) {
      themeHost.innerHTML = THEMES.map((theme) => {
        const active = wizardPicks.themeId === theme.id ? " active" : "";
        return (
          `<button type="button" class="wizard-pill${active}" data-theme-id="${theme.id}">` +
          `<span class="code">${theme.icon}</span>` +
          `<span>${t(theme.nameKey)}</span>` +
          `</button>`
        );
      }).join("");
      themeHost
        .querySelectorAll<HTMLButtonElement>(".wizard-pill")
        .forEach((btn) => {
          btn.addEventListener("click", () => {
            const id = btn.dataset.themeId;
            if (id) {
              wizardPicks.themeId = id;
              renderWizard();
            }
          });
        });
    }
    // Language section visibility — hide for mono themes (Géographie etc)
    // since lang pair and CEFR don't apply.
    const activeTheme = getTheme(wizardPicks.themeId);
    const languageSection = byId("wizardLanguagesSection");
    if (languageSection) {
      languageSection.classList.toggle("hidden", activeTheme.shape !== "bilingual");
    }
    // Locale select — always shown in the language's native name so the
    // user can recognise their language regardless of current UI locale.
    const currentLocaleVal = wizardPicks.locale || "";
    if (wizardLocaleSelect) {
      wizardLocaleSelect.innerHTML =
        `<option value="" disabled${currentLocaleVal ? "" : " selected"}>—</option>` +
        LANGS.map((lang) => {
          const code = LANG_TO_LOCALE[lang];
          const sel = currentLocaleVal === code ? " selected" : "";
          return `<option value="${code}"${sel}>${LANG_NATIVE[lang]}</option>`;
        }).join("");
      wizardLocaleSelect.onchange = () => {
        wizardPicks.locale = wizardLocaleSelect.value;
        localStorage.setItem(STORAGE_KEYS.LOCALE, wizardPicks.locale);
        deps.applyLocale();
        renderWizard();
      };
    }
    // Target select (all 3 langs, can pick any).
    const currentTarget = wizardPicks.target || "";
    if (wizardTargetSelect) {
      wizardTargetSelect.innerHTML =
        `<option value="" disabled${currentTarget ? "" : " selected"}>—</option>` +
        LANGS.map((lang) => {
          const sel = currentTarget === lang ? " selected" : "";
          return `<option value="${lang}"${sel}>${LANG_NAMES[loc][lang]}</option>`;
        }).join("");
      wizardTargetSelect.onchange = () => {
        wizardPicks.target = wizardTargetSelect.value as Lang;
        if (wizardPicks.source === wizardPicks.target) {
          wizardPicks.source = null;
        }
        renderWizard();
      };
    }
    // Source select — defaults to picked locale's lang IF that lang != target.
    const localeLang = wizardPicks.locale
      ? LOCALE_TO_LANG[wizardPicks.locale as keyof typeof LOCALE_TO_LANG]
      : null;
    let effectiveSource = wizardPicks.source;
    if (
      effectiveSource == null &&
      localeLang &&
      wizardPicks.target &&
      localeLang !== wizardPicks.target
    ) {
      effectiveSource = localeLang;
    }
    if (wizardSourceSelect) {
      wizardSourceSelect.innerHTML =
        `<option value="" disabled${effectiveSource ? "" : " selected"}>—</option>` +
        LANGS.map((lang) => {
          const disabled = lang === wizardPicks.target ? " disabled" : "";
          const sel = effectiveSource === lang ? " selected" : "";
          return `<option value="${lang}"${sel}${disabled}>${LANG_NAMES[loc][lang]}</option>`;
        }).join("");
      wizardSourceSelect.onchange = () => {
        wizardPicks.source = wizardSourceSelect.value as Lang;
        renderWizard();
      };
    }
    // Level pills — "Débutant complet" first, then A1..C2.
    // "Débutant complet" maps to `levelFloor = "none"` (no override — the
    // adaptive estimator starts from A1 as usual). Any A1-C2 pill acts as
    // a floor: the estimator can still promote above, but never drops
    // below. Default: "none" (pre-selected).
    const levelOpts: { key: LevelFloorPick; label: string }[] = [
      { key: "none", label: t("wizard_level_none") as string },
    ];
    for (const lv of LEVELS) levelOpts.push({ key: lv, label: lv });
    if (wizardLevelPills) {
      wizardLevelPills.innerHTML = levelOpts
        .map((o) => {
          const active = wizardPicks.levelFloor === o.key ? " active" : "";
          return `<button type="button" class="wizard-pill${active}" data-level="${o.key}">${o.label}</button>`;
        })
        .join("");
      wizardLevelPills
        .querySelectorAll<HTMLButtonElement>(".wizard-pill")
        .forEach((btn) => {
          btn.addEventListener("click", () => {
            wizardPicks.levelFloor = btn.dataset.level as LevelFloorPick;
            renderWizard();
          });
        });
    }
    // Direction preset pills — replaces the old free-form slider. The
    // underlying wizardPicks.dirRatio stays a 0-100 number; the picker
    // just constrains the user's choice to 3 values.
    const wizardDirPresets = byId("wizardDirPresets");
    if (wizardDirPresets) {
      // Wizard is always bilingual (it runs before any deck switch, so
      // the active deck is DEFAULT_LIST_ID = general = bilingual). Use
      // the user's picked target / source lang short codes as the face
      // labels. Fall back to generic "A"/"B" if a pick is still null
      // mid-wizard (the picker still works, just abstract).
      const localeLang = wizardPicks.locale
        ? LOCALE_TO_LANG[wizardPicks.locale as keyof typeof LOCALE_TO_LANG]
        : null;
      const sourceLang: Lang | null = wizardPicks.source ?? localeLang ?? null;
      const targetLang: Lang | null = (wizardPicks.target as Lang) || null;
      const from = sourceLang ? LANG_SHORT[sourceLang] : "A";
      const to = targetLang ? LANG_SHORT[targetLang] : "B";
      renderDirPresets(
        wizardDirPresets,
        wizardPicks.dirRatio,
        (v) => {
          wizardPicks.dirRatio = v;
        },
        [from, to],
      );
    }
    // Update labels (in case locale just changed).
    const setText = (id: string, val: string): void => {
      const el = byId(id);
      if (el) el.textContent = val;
    };
    setText("wizardTitle", t("wizard_title") as string);
    setText("wizardSub", t("wizard_sub") as string);
    setText("wizardIntro", t("wizard_intro") as string);
    if (wizardNameLabel) wizardNameLabel.textContent = t("wizard_name_label") as string;
    if (wizardNameInput) wizardNameInput.placeholder = t("wizard_name_ph") as string;
    setText("wizardLocaleLabel", t("wizard_locale_label") as string);
    setText("wizardFontSizeLabel", t("font_size_label") as string);
    setText("wizardThemeLabel", t("wizard_theme_label") as string);
    setText("wizardTargetLabel", t("wizard_target_label") as string);
    setText("wizardSourceLabel", t("wizard_source_label") as string);
    setText("wizardDirLabel", t("dir_preset_label") as string);
    setText("wizardLevelLabel", t("wizard_level_label") as string);
    if (wizardContinue) wizardContinue.textContent = t("wizard_continue") as string;
  }

  // Wizard font size + dir slider wiring — runs once per factory.
  // Keeping this inline (instead of at module top-level) leaves the
  // module side-effect-free at import time: nothing runs until
  // createWizard() is called from bootstrap.
  const wFsUp = byId<HTMLButtonElement>("wizardFontSizeUp");
  const wFsDown = byId<HTMLButtonElement>("wizardFontSizeDown");
  const wFsValue = byId("wizardFontSizeValue");
  function syncWizardFs(): void {
    if (wFsValue) wFsValue.textContent = Math.round(loadFontScale() * 100) + "%";
  }
  if (wFsUp) {
    wFsUp.addEventListener("click", () => {
      changeFontScale(1);
      syncWizardFs();
    });
  }
  if (wFsDown) {
    wFsDown.addEventListener("click", () => {
      changeFontScale(-1);
      syncWizardFs();
    });
  }
  function showWizardError(msg: string): void {
    if (wizardError) {
      wizardError.textContent = msg;
      show(wizardError);
    }
  }
  function clearWizardError(): void {
    if (wizardError) {
      hide(wizardError);
      wizardError.textContent = "";
    }
  }

  function startWizard(): void {
    // Pre-fill from any existing saved values (lets user re-run wizard later).
    wizardPicks.locale = localStorage.getItem(STORAGE_KEYS.LOCALE) || null;
    wizardPicks.themeId = getTheme(
      localStorage.getItem(STORAGE_KEYS.THEME_ID) || DEFAULT_THEME_ID,
    ).id;
    wizardPicks.target = deps.getTargetLang() || null;
    wizardPicks.source = deps.getSourceLang() || null;
    wizardPicks.userName =
      deps.getUserName() || localStorage.getItem(STORAGE_KEYS.USER) || null;
    // Pre-select the existing floor if any; otherwise default to "none"
    // (Débutant complet) so a fresh user has a valid pick without clicking.
    wizardPicks.levelFloor = (deps.getLevelFloor() || "none") as LevelFloorPick;
    if (wizardNameInput) wizardNameInput.value = wizardPicks.userName || "";
    clearWizardError();
    renderWizard();
    syncWizardFs();
    show(wizardScreen);
  }

  function finishWizard(): void {
    clearWizardError();
    const typedName = wizardNameInput?.value.trim() || "";
    if (!typedName) {
      showWizardError(t("wizard_pick_name") as string);
      wizardNameInput?.focus();
      return;
    }
    wizardPicks.userName = typedName;
    if (!wizardPicks.locale) wizardPicks.locale = "fr";
    if (!wizardPicks.themeId) {
      showWizardError(t("wizard_pick_theme") as string);
      return;
    }
    const theme = getTheme(wizardPicks.themeId);
    const isBilingualTheme = theme.shape === "bilingual";
    // Bilingual themes require target + source + a valid default deck
    // for their CEFR / dir-ratio settings. Mono themes commit only the
    // user-level prefs + the theme + its default deck.
    let source: Lang | null = null;
    if (isBilingualTheme) {
      if (!wizardPicks.target) {
        showWizardError(t("wizard_pick_target") as string);
        return;
      }
      const localeLang =
        LOCALE_TO_LANG[wizardPicks.locale as keyof typeof LOCALE_TO_LANG];
      source = wizardPicks.source;
      if (!source && localeLang && localeLang !== wizardPicks.target) {
        source = localeLang;
      }
      if (!source) {
        showWizardError(t("wizard_pick_source") as string);
        return;
      }
    }
    // Commit to localStorage — shared fields first.
    localStorage.setItem(STORAGE_KEYS.LOCALE, wizardPicks.locale);
    localStorage.setItem(STORAGE_KEYS.WIZARD, "1");
    localStorage.setItem(STORAGE_KEYS.USER, wizardPicks.userName);
    localStorage.setItem(STORAGE_KEYS.THEME_ID, theme.id);
    const defaultDeck = defaultListForTheme(theme.id);
    localStorage.setItem(STORAGE_KEYS.LIST_ID, defaultDeck.id);
    // Sync in-memory state so loadList() below reads the fresh picks.
    deps.setThemeId(theme.id);
    deps.setListId(defaultDeck.id);
    if (isBilingualTheme && wizardPicks.target && source) {
      localStorage.setItem(STORAGE_KEYS.TARGET, wizardPicks.target);
      localStorage.setItem(STORAGE_KEYS.SOURCE, source);
      // Level floor: persist only if user picked an explicit CEFR
      // level. "none" (Débutant complet) clears any previous override.
      // Scoped to the theme's default deck (the one they'll land on).
      if (wizardPicks.levelFloor && wizardPicks.levelFloor !== "none") {
        saveLevelFloor(defaultDeck.id, wizardPicks.levelFloor);
        deps.setLevelFloor(wizardPicks.levelFloor);
        // Seed userLevel too so the card picker is biased from tick 0.
        deps.setUserLevel(wizardPicks.levelFloor);
      } else {
        saveLevelFloor(defaultDeck.id, null);
        deps.setLevelFloor(null);
      }
      // Persist direction ratio (default 20 = 80% target→source).
      const dr = Math.max(0, Math.min(100, wizardPicks.dirRatio | 0));
      saveDirRatio(defaultDeck.id, dr);
      deps.setDirRatio(dr);
      deps.setTargetLang(wizardPicks.target);
      deps.setSourceLang(source);
    }
    deps.setUserName(wizardPicks.userName);
    deps.applyLocale();
    hide(wizardScreen);
    // Show the guided tour once, unless the user has already seen it
    // (e.g. they re-ran the wizard later). The tour needs the main
    // screen painted to measure header targets, so we kick off the list
    // load first and launch the tour on the next paint.
    const shouldTour = localStorage.getItem(STORAGE_KEYS.INTRO) !== "1";
    deps.loadList();
    if (shouldTour) {
      // Skip the intro slide — the pre-wizard intro screen already
      // showed the user what the app is, and seeing the same copy
      // again as step 1/8 would feel redundant.
      requestAnimationFrame(() => openTour({ skipIntro: true }));
    }
  }

  if (wizardContinue) {
    wizardContinue.addEventListener("click", finishWizard);
  }

  return { startWizard, finishWizard };
}
